import React, { useState, useRef, useEffect } from "react";
import DateRangePicker from "../Daterangepicker";

// ─── Static constants ─────────────────────────────────────────────────────────

const WORKER_TYPES = ["Registered", "Unregistered"];
const GENDERS      = ["(Blanks)", "Male", "Female", "Others"];
const SOURCES      = ["(Blanks)", "App", "Field Agent", "WhatsApp"];

const CATEGORY_OPTIONS = [
  { key: "", label: "All" },
  { key: "onboarding", label: "Onboarding" },
  { key: "refcheck", label: "RefCheck" },
  { key: "retention", label: "Retention" },
  { key: "companyScreening", label: "Company Screening" },
  { key: "eshram", label: "E-Shram" },
];

// ─── DisabledTooltip ──────────────────────────────────────────────────────────

function DisabledTooltip({ reason }) {
  const [pos, setPos] = useState(null);
  const iconRef = useRef(null);

  const show = () => {
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    setPos({
      top: rect.top + window.scrollY,
      left: rect.left + rect.width / 2 + window.scrollX,
    });
  };

  const hide = () => setPos(null);

  return (
    <div className="relative inline-flex" onMouseEnter={show} onMouseLeave={hide}>
      <svg
        ref={iconRef}
        className="w-3.5 h-3.5 text-amber-400 cursor-help flex-shrink-0"
        fill="none" stroke="currentColor" viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>

      {pos && (
        <div
          className="pointer-events-none"
          style={{
            position: "fixed",
            top: pos.top - 8,
            left: pos.left,
            transform: "translate(-50%, -100%)",
            zIndex: 9999,
          }}
        >
          <div
            className="bg-gray-900 text-white text-xs font-medium rounded-lg px-3 py-2 shadow-xl text-center leading-snug"
            style={{ width: "max-content", maxWidth: "260px", whiteSpace: "normal" }}
          >
            {reason}
          </div>
          <div className="flex justify-center -mt-px">
            <div className="w-2 h-2 bg-gray-900 rotate-45" />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MultiSelectDropdown ──────────────────────────────────────────────────────

function MultiSelectDropdown({
  options,
  selected,
  onSelect,
  placeholder,
  disabled = false,
  loading = false,
  disabledReason,
  getOptionLabel = (opt) => typeof opt === 'string' ? opt : opt.displayName || opt.name,
  getOptionValue = (opt) => typeof opt === 'string' ? opt : opt._id || opt.name,
  getSelectedLabel = null,
  isOptionDisabled = () => false,
  excludeFromSelectAll = [] // ← NEW: array of values to exclude from "Select All"
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (disabled && isOpen) setIsOpen(false);
  }, [disabled, isOpen]);

  // Filter options that should be considered for "Select All"
  const selectableOptions = options.filter(opt => {
    const value = getOptionValue(opt);
    return !excludeFromSelectAll.includes(value);
  });
  
  const allSelectableSelected = selectableOptions.length > 0 && 
    selectableOptions.every(opt => selected.includes(getOptionValue(opt)));

  const handleSelectAllToggle = () => {
    if (allSelectableSelected) {
      // Remove all selectable options
      const newSelected = selected.filter(item => !selectableOptions.some(opt => getOptionValue(opt) === item));
      onSelect(newSelected);
    } else {
      // Add all selectable options that aren't already selected and aren't disabled
      const selectableValues = selectableOptions
        .filter(opt => !isOptionDisabled(getOptionValue(opt)))
        .map(opt => getOptionValue(opt));
      const newSelected = [...new Set([...selected, ...selectableValues])];
      onSelect(newSelected);
    }
  };

  const handleOptionToggle = (option) => {
    const optionValue = getOptionValue(option);
    const optionDisabled = isOptionDisabled(optionValue);
    
    if (optionDisabled) return;
    
    if (selected.includes(optionValue)) {
      onSelect(selected.filter((item) => item !== optionValue));
    } else {
      onSelect([...selected, optionValue]);
    }
  };

  const getDisplayText = () => {
    if (loading) return "Loading...";
    if (options.length === 0) return placeholder;
    if (selected.length === 0) return placeholder;
    
    if (getSelectedLabel) {
      return getSelectedLabel();
    }
    
    if (selected.length === 1) {
      const option = options.find(opt => getOptionValue(opt) === selected[0]);
      return option ? getOptionLabel(option) : selected[0];
    }
    
    if (selected.length === options.length) return `All ${placeholder.toLowerCase()}`;
    return `${selected.length} selected`;
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`p-2 border rounded flex justify-between items-center transition-colors ${
          disabled
            ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-60 border-gray-200"
            : "bg-white cursor-pointer hover:border-gray-400 border-gray-300"
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className="text-sm truncate">{getDisplayText()}</span>
        {loading ? (
          <svg className="w-4 h-4 ml-2 flex-shrink-0 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        ) : (
          <svg
            className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""} ${disabled ? "text-gray-300" : ""}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-10 mt-1 w-full max-h-[200px] overflow-y-auto border border-gray-300 rounded-md bg-white shadow-lg">
          {!loading && selectableOptions.length > 0 && (
            <div
              className="px-3 py-2 cursor-pointer flex items-center border-b border-gray-200 bg-gray-50 hover:bg-gray-100"
              onClick={handleSelectAllToggle}
            >
              <input 
                type="checkbox" 
                checked={allSelectableSelected} 
                onChange={handleSelectAllToggle} 
                className="w-4 h-4 mr-2 cursor-pointer" 
              />
              <span className="text-sm font-medium cursor-pointer">
                {allSelectableSelected ? "Deselect All" : "Select All"}
              </span>
            </div>
          )}
          {loading ? (
            <div className="px-3 py-4 text-center text-gray-500 text-sm">Loading options...</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-4 text-center text-gray-500 text-sm">No options available</div>
          ) : (
            options.map((option, index) => {
              const optionValue = getOptionValue(option);
              const isSelected = selected.includes(optionValue);
              const optionDisabled = isOptionDisabled(optionValue);
              
              return (
                <div
                  key={optionValue || index}
                  className={`px-3 py-2 cursor-pointer flex items-center ${
                    optionDisabled
                      ? "bg-gray-100 opacity-50 cursor-not-allowed"
                      : isSelected
                      ? "bg-blue-50 hover:bg-gray-100"
                      : "hover:bg-gray-100"
                  }`}
                  onClick={() => handleOptionToggle(option)}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleOptionToggle(option)}
                    className={`w-4 h-4 mr-2 ${optionDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                    disabled={optionDisabled}
                  />
                  <span className={`text-sm ${optionDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
                    {getOptionLabel(option)}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ─── CustomDropdown ───────────────────────────────────────────────────────────

function CustomDropdown({ options, selected, onSelect, placeholder, disabled = false, disabledReason }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (disabled && isOpen) setIsOpen(false);
  }, [disabled, isOpen]);

  const handleSelect = (option) => {
    // Call onSelect if it's a function
    if (typeof onSelect === 'function') {
      onSelect(option);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`p-2 border rounded flex justify-between items-center transition-colors ${
          disabled
            ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-60 border-gray-200"
            : "bg-white cursor-pointer hover:border-gray-400 border-gray-300"
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className="text-sm">{disabled ? placeholder : (selected || placeholder)}</span>
        <svg
          className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""} ${disabled ? "text-gray-300" : ""}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-10 mt-1 w-full max-h-[200px] overflow-y-auto border border-gray-300 rounded-md bg-white shadow-lg">
          {options.map((option, index) => (
            <div
              key={index}
              className={`px-3 py-2 text-sm cursor-pointer ${
                selected === option ? "bg-blue-50" : "hover:bg-gray-100"
              }`}
              onClick={() => handleSelect(option)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── FilterLabel ──────────────────────────────────────────────────────────────

function FilterLabel({ children, loading, disabled, lockReason }) {
  return (
    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
      {children}
      {loading && <span className="text-xs text-blue-500 font-normal">(Loading...)</span>}
      {!loading && disabled && lockReason && (
        <DisabledTooltip reason={lockReason} />
      )}
    </label>
  );
}

// ─── Helper ───────────────────────────────────────────────────────────────────

const addBlanksOption = (options) => {
  if (!options || options.length === 0) return ["(Blanks)"];
  return options.some(opt => opt === "(Blanks)") ? options : ["(Blanks)", ...options];
};

// ─── API-backed filters ───────────────────────────────────────────────────────

export function LocationFilter({ selected, onSelect, resetResults, options = [], loading = false }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel loading={loading}>Location</FilterLabel>
      <MultiSelectDropdown
        options={options.filter(opt => opt !== "(Blanks)")}  // Filter out blanks
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults(); }}
        placeholder="Select Location"
        loading={loading}
      />
    </div>
  );
}

export function SkillsFilter({ selected, onSelect, resetResults, options = [], loading = false }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel loading={loading}>Skills</FilterLabel>
      <MultiSelectDropdown
        options={options}  // ← Removed addBlanksOption wrapper
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults(); }}
        placeholder="Select Skills"
        loading={loading}
      />
    </div>
  );
}

export function LastCompanyFilter({ 
  selected, 
  onSelect, 
  resetResults, 
  options = [], 
  loading = false, 
  disabled = false, 
  disabledReason,
  setActiveCompanyFilter,
  activeCompanyFilter
}) {
  const getOptionLabel = (company) => {
    if (!company) return '';
    if (company === "NO_COMPANY") return "No Company";
    return company.displayName || (company.code ? `${company.name} (${company.code})` : company.name);
  };

  const getOptionValue = (company) => {
    if (company === "NO_COMPANY") return "blank";
    return company._id || company.name;
  };

  const getSelectedLabel = () => {
    if (selected.length === 0) return "Select Last Company";
    if (selected.length === 1) {
      if (selected[0] === "blank") return "No Company";
      const company = options.find(c => c._id === selected[0]);
      return company ? getOptionLabel(company) : selected[0];
    }
    // Check if all companies (excluding "No Company") are selected
    const allCompaniesSelected = options.length > 0 && 
      selected.length === options.length && 
      !selected.includes("blank");
    if (allCompaniesSelected) return `All companies`;
    return `${selected.length} selected`;
  };

  const handleSelect = (newSelected) => {
    // Check if "No Company" (blank) is being added
    const noCompanyAdded = newSelected.includes("blank") && !selected.includes("blank");
    // Check if "No Company" is being removed
    const noCompanyRemoved = !newSelected.includes("blank") && selected.includes("blank");
    
    if (noCompanyAdded) {
      // If adding "No Company", clear all other selections and only keep "No Company"
      onSelect(["blank"]);
      setActiveCompanyFilter("last");
    } else if (noCompanyRemoved) {
      // If removing "No Company", just update with the new selection
      onSelect(newSelected);
      if (newSelected.length === 0) {
        setActiveCompanyFilter(null);
      } else {
        setActiveCompanyFilter("last");
      }
    } else if (newSelected.includes("blank") && newSelected.length > 1) {
      // If trying to select "No Company" with other options, only keep "No Company"
      onSelect(["blank"]);
      setActiveCompanyFilter("last");
    } else {
      // Normal selection
      onSelect(newSelected);
      if (newSelected.length > 0) {
        setActiveCompanyFilter("last");
      } else {
        setActiveCompanyFilter(null);
      }
    }
    resetResults();
  };

  const isOptionDisabled = (optionValue) => {
    if (disabled) return true;
    // Only disable other options if "No Company" is selected AND it's the only selection
    if (selected.includes("blank") && selected.length === 1 && optionValue !== "blank") {
      return true;
    }
    return false;
  };

  // Prepare options with "No Company" at the top
  const enhancedOptions = [
    "NO_COMPANY",
    ...options
  ];

  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel loading={loading} disabled={disabled} lockReason={disabledReason}>Last Company</FilterLabel>
      <MultiSelectDropdown
        options={enhancedOptions}
        selected={selected}
        onSelect={handleSelect}
        placeholder="Select Last Company"
        loading={loading}
        disabled={disabled}
        disabledReason={disabledReason}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        getSelectedLabel={getSelectedLabel}
        isOptionDisabled={isOptionDisabled}
        excludeFromSelectAll={["blank"]} 
      />
    </div>
  );
}

export function AllTimeCompanyFilter({ selected, onSelect, resetResults, options = [], loading = false, disabled = false, disabledReason }) {
  const getOptionLabel = (company) => {
    if (!company) return '';
    return company.displayName || (company.code ? `${company.name} (${company.code})` : company.name);
  };

  const getSelectedLabel = () => {
    if (selected.length === 0) return "Select Company (excl. last)";
    if (selected.length === 1) {
      const company = options.find(c => c._id === selected[0]);
      return company ? getOptionLabel(company) : selected[0];
    }
    if (selected.length === options.length && options.length > 0) return `All companies`;
    return `${selected.length} selected`;
  };

  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel loading={loading} disabled={disabled} lockReason={disabledReason}>All-time Company</FilterLabel>
      <MultiSelectDropdown
        options={options}
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults(); }}
        placeholder="Select Company (excl. last)"
        loading={loading}
        disabled={disabled}
        disabledReason={disabledReason}
        getOptionLabel={getOptionLabel}
        getSelectedLabel={getSelectedLabel}
      />
    </div>
  );
}

// ─── Static filters ───────────────────────────────────────────────────────────

export function WorkerTypeFilter({ selected, onSelect, resetResults }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Worker Type</FilterLabel>
      <MultiSelectDropdown
        options={WORKER_TYPES}
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults(); }}
        placeholder="Select Worker Type"
      />
    </div>
  );
}

export function GenderFilter({ selected, onSelect, resetResults }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Gender</FilterLabel>
      <MultiSelectDropdown
        options={GENDERS}
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults(); }}
        placeholder="Select Gender"
      />
    </div>
  );
}

export function SourceFilter({ selected, onSelect, resetResults, options = [], loading = false }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel loading={loading}>Source</FilterLabel>
      <MultiSelectDropdown
        options={options}
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults(); }}
        placeholder="Select Source"
        loading={loading}
      />
    </div>
  );
}

export function AadhaarFilter({ selected, onSelect, resetResults }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Aadhaar Verified</FilterLabel>
      <CustomDropdown
        options={["All", "Yes", "No"]}
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults(); }}
        placeholder="All"
      />
    </div>
  );


}


export function QualificationFilter({ selected, onSelect, resetResults }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Highest Qualification</FilterLabel>
      <CustomDropdown
        options={[ 
    "All",
    "No Formal Education",
    "10th",
    "12th",
    "Graduation",
    "P.G. (Postgraduates)",
    "Diploma",
    "ITI",
    "Other"]}
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults(); }}
        placeholder="All"
      />
    </div>
  );
}

export function KycFilter({ selected, onSelect, resetResults }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>KYC</FilterLabel>
      <CustomDropdown
        options={["All", "Done", "Pending"]}
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults(); }}
        placeholder="All"
      />
    </div>
  );
}

export function RefCheckFilter({ selected, onSelect, resetResults }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Ref Check Status</FilterLabel>
      <CustomDropdown
        options={["All", "Done", "Pending"]}
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults(); }}
        placeholder="All"
      />
    </div>
  );
}

export function CompanyCheckFilter({ selected, onSelect, resetResults, disabled = false, disabledReason }) {
  const displayToActual = {
    "All": "All",
    "Employed": "Cleared",
    "Not Employed": "Pending"
  };
  
  const actualToDisplay = {
    "All": "All",
    "Cleared": "Employed",
    "Pending": "Not Employed"
  };

  const displaySelected = actualToDisplay[selected] || selected;

  const handleSelect = (displayValue) => {
    const actualValue = displayToActual[displayValue];
    onSelect(actualValue);
    resetResults();
  };

  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel disabled={disabled} lockReason={disabledReason}>Company Check</FilterLabel>
      <CustomDropdown
        options={["All", "Employed", "Not Employed"]}
        selected={displaySelected}
        onSelect={handleSelect}
        placeholder="All"
        disabled={disabled}
        disabledReason={disabledReason}
      />
    </div>
  );
}

export function CompanyLastWorkedOnFilter({ startDate, endDate, onChange, resetResults, disabled = false, disabledReason, companyName }) {
  const shortLabel = "Last Worked On (Company)";

  return (
    <div className="flex flex-col space-y-2">
      <div className="flex items-center gap-1.5">
        <label
          className="text-sm font-medium text-gray-700"
          title={companyName ? `Last Worked On (${companyName})` : shortLabel}
        >
          {companyName ? (
            <>
              Last Worked On{" "}
              <span className="text-indigo-600 font-semibold">({companyName})</span>
            </>
          ) : (
            shortLabel
          )}
        </label>
        {disabled && disabledReason && <DisabledTooltip reason={disabledReason} />}
      </div>
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onChange={(v) => { onChange(v); resetResults(); }}
        maxDate={new Date().toISOString().split("T")[0]}
        placeholder="Select date range"
        disabled={disabled}
      />
    </div>
  );
}

export function BlacklistedFilter({ selected, onSelect, resetResults }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Blacklisted</FilterLabel>
      <CustomDropdown
        options={["All", "Yes", "No"]}
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults(); }}
        placeholder="All"
      />
    </div>
  );
}

export function RegisteredOnFilter({ startDate, endDate, onChange, resetResults }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Registered On</FilterLabel>
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onChange={(v) => { onChange(v); resetResults(); }}
        maxDate={new Date().toISOString().split("T")[0]}
        placeholder="Select date range"
      />
    </div>
  );
}

export function LastWorkedOnFilter({ startDate, endDate, onChange, resetResults }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Last Worked On</FilterLabel>
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onChange={(v) => { onChange(v); resetResults(); }}
        maxDate={new Date().toISOString().split("T")[0]}
        placeholder="Select date range"
      />
    </div>
  );
}
export function HasAppTokenFilter({ selected, onSelect, resetResults }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Has App Token</FilterLabel>
      <CustomDropdown
        options={["All", "Yes", "No"]}
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults?.(); }}
        placeholder="All"
      />
    </div>
  );
}

export function LastCallStatusFilter({ selected, onSelect, resetResults, options = [], loading = false , disabled = false, disabledReason }) {
  return (
    <div className="flex flex-col space-y-2">
      <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
        Last Call Status
        {loading && <span className="text-xs text-blue-500 font-normal">(Loading...)</span>}
         {!loading && disabled && disabledReason && <DisabledTooltip reason={disabledReason} />}
      </label>
      <CustomDropdown
        options={options}
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults(); }}
        placeholder="Select Status"
        disabled={loading || options.length <= 1}
        disabledReason={disabledReason}
      />
    </div>
  );
}


export function LastCallSubStatusFilter({ selected, onSelect, resetResults, options = [], loading = false, disabled = false, disabledReason }) {
  return (
    <div className="flex flex-col space-y-2">
      <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
        Last Call Sub Status
        {loading && <span className="text-xs text-blue-500 font-normal">(Loading...)</span>}
        {!loading && disabled && disabledReason && <DisabledTooltip reason={disabledReason} />}
      </label>
      <CustomDropdown
        options={options}
        selected={selected}
        onSelect={(v) => { onSelect(v); resetResults(); }}
        placeholder="Select Status"
        disabled={disabled || options.length <= 1}
        disabledReason={disabledReason}
      />
    </div>
  );
}


export function CallCategoryFilter({ selected, onSelect, resetResults }) {
  const selectedLabel =
    CATEGORY_OPTIONS.find((opt) => opt.key === selected)?.label ?? "All";

  const handleSelect = (label) => {
    const matched = CATEGORY_OPTIONS.find((opt) => opt.label === label);
    onSelect(matched ? matched.key : "");
    resetResults();
  };

  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Call Category</FilterLabel>
      <CustomDropdown
        options={CATEGORY_OPTIONS.map((opt) => opt.label)}
        selected={selectedLabel}
        onSelect={handleSelect}
        placeholder="All"
      />
    </div>
  );
}

export function PaymentverificationFilter({ selected, onSelect, resetResults }) {
  const displayToBackend = {
    "All": "All", 
    "Not Verified": "failed",
    "Not Checked": "not checked", 
    "Verified": "success"
  };
  
  const backendToDisplay = {
    "All": "All", 
    "failed": "Not Verified",
    "not checked": "Not Checked",
    "success": "Verified"
  };
  
  const displaySelected = backendToDisplay[selected] ||  "All";

  const handleSelect = (displayValue) => {
    const backendValue = displayToBackend[displayValue];
    onSelect(backendValue);
    resetResults?.();
  };

  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Account Verification</FilterLabel>
      <CustomDropdown
        options={["All", "Not Verified", "Not Checked", "Verified"]}
        selected={displaySelected}
        onSelect={handleSelect}
        placeholder="All"
      />
    </div>
  );
}



export function ShiftCountFilter({ minValue, maxValue, onChange, resetResults }) {
  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Shift Count</FilterLabel>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          min="0"
          value={minValue}
          onChange={(e) => {
            const raw = e.target.value;
            onChange({ min: raw === "" ? "" : Math.max(0, parseInt(raw, 10)), max: maxValue });
            resetResults?.();
          }}
          placeholder="Min"
          className="w-full p-2 border border-gray-300 rounded text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
        />
        <span className="text-gray-400 text-xs flex-shrink-0">to</span>
        <input
          type="number"
          min="0"
          value={maxValue}
          onChange={(e) => {
            const raw = e.target.value;
            onChange({ min: minValue, max: raw === "" ? "" : Math.max(0, parseInt(raw, 10)) });
            resetResults?.();
          }}
          placeholder="Max"
          className="w-full p-2 border border-gray-300 rounded text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
        />
      </div>
    </div>
  );
}

export function PoliceVerificationFilter({ selected, onSelect, resetResults }) {
  const displayToBackend = {
    "All": "All",
    "Pass": "pass",
    "Failed": "failed",
    "Not Verified": "not verified",
    "Not Uploaded": "not uploaded"
  };
  
  const backendToDisplay = {
    "All": "All",
    "pass": "Pass",
    "failed": "Failed",
    "not verified": "Not Verified",
    "not uploaded": "Not Uploaded"
  };
  
  const displaySelected = backendToDisplay[selected] || "All";

  const handleSelect = (displayValue) => {
    const backendValue = displayToBackend[displayValue];
    if (typeof onSelect === 'function') onSelect(backendValue);
    if (typeof resetResults === 'function') resetResults();
  };

  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Police Verification</FilterLabel>
      <CustomDropdown
        options={["All", "Pass", "Failed", "Not Verified", "Not Uploaded"]}
        selected={displaySelected}
        onSelect={handleSelect}
        placeholder="All"
      />
    </div>
  );
}

export function MarksheetFilter({ selected, onSelect, resetResults }) {
  const displayToBackend = {
    "All": "All",
    "Pass": "pass",
    "Failed": "failed",
    "Not Verified": "not verified",
    "Not Uploaded": "not uploaded"
  };
  
  const backendToDisplay = {
    "All": "All",
    "pass": "Pass",
    "failed": "Failed",
    "not verified": "Not Verified",
    "not uploaded": "Not Uploaded"
  };
  
  const displaySelected = backendToDisplay[selected] || "All";

  const handleSelect = (displayValue) => {
    const backendValue = displayToBackend[displayValue];
    if (typeof onSelect === 'function') onSelect(backendValue);
    if (typeof resetResults === 'function') resetResults();
  };

  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>Educational Certificate</FilterLabel> 
      <CustomDropdown
        options={["All", "Pass", "Failed", "Not Verified", "Not Uploaded"]}
        selected={displaySelected}
        onSelect={handleSelect}
        placeholder="All"
      />
    </div>
  );
}


export function EShramFilter({ selected, onSelect, resetResults }) {
  const handleSelect = (value) => {
    if (typeof onSelect === 'function') onSelect(value);
    if (typeof resetResults === 'function') resetResults();
  };

  return (
    <div className="flex flex-col space-y-2">
      <FilterLabel>eShram</FilterLabel>
      <CustomDropdown
        options={["All", "Yes", "No"]}
        selected={selected}
        onSelect={handleSelect}
        placeholder="All"
      />
    </div>
  );
}